/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * useUnifiedWallet Hook — SINGLETON PATTERN
 * ============================================
 * Provider-agnostic wallet connection manager.
 * Accepts any EIP-1193 provider (from EIP-6963 discovery or WalletConnect)
 * and normalizes it into a consistent interface for the rest of the app.
 *
 * WHY SINGLETON?
 * This hook is called from multiple components (ANSWalletButton ×3 via Navbar,
 * WalletContext, etc.). Each call to a regular hook creates independent state —
 * so when one instance connects, the others don't know about it.
 *
 * The singleton pattern stores all wallet state at MODULE SCOPE. Every call to
 * useUnifiedWallet() subscribes to the same shared state. When any instance
 * connects/disconnects, ALL subscribers re-render with the new state.
 *
 * Responsibilities:
 * - Connect/disconnect with any provider source
 * - Chain validation and switching for Apertum
 * - Event listener management (works for both injected and WC providers)
 * - WalletConnect session lifecycle
 * - Exposes ethers.js provider + signer for contract interactions
 * - Connect only after the visitor clicks the wallet button (no auto-reconnect)
 *
 * @version 2.0.0 — Singleton rewrite
 * Place in: src/hooks/useUnifiedWallet.ts
 */

import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { toast } from 'react-toastify';
import {
  APERTUM_CHAIN,
  WALLETCONNECT_PAIR_CHAIN,
  WALLETCONNECT_PROJECT_ID,
  getAddChainParams,
  getWalletConnectMetadata,
  isWalletConnectConfigured,
} from '../config/walletConfig';
import { pinWalletConnectToApertum } from '../utils/apertumWalletChain';
import {
  clearWalletConnectStorage,
  teardownWalletConnectProvider,
} from '../utils/walletConnectSession';
import { parseChainId } from '../web3/apertumChain';
import type { EIP1193Provider, DiscoveredWallet } from './useWalletDiscovery';

// =============================================================================
// TYPES
// =============================================================================

export type ConnectionSource = 'injected' | 'walletconnect' | null;

export interface UnifiedWalletState {
  /** Connected wallet address (checksummed) */
  address: string | null;
  /** Current chain ID */
  chainId: number | null;
  /** Whether on the correct chain (Apertum) */
  isCorrectChain: boolean;
  /** Whether a wallet is connected */
  isConnected: boolean;
  /** Whether a connection attempt is in progress */
  isConnecting: boolean;
  /** How the wallet was connected */
  connectionSource: ConnectionSource;
}

export interface UnifiedWalletActions {
  connect: (wallet: DiscoveredWallet) => Promise<void>;
  disconnect: () => Promise<void>;
  switchToApertum: () => Promise<void>;
  getProvider: () => ethers.providers.Web3Provider | null;
  getSigner: () => ethers.providers.JsonRpcSigner | null;
  getRawProvider: () => EIP1193Provider | null;
}

export type UnifiedWalletHook = UnifiedWalletState & UnifiedWalletActions;

// =============================================================================
// SINGLETON STATE (lives outside React — shared by ALL hook instances)
// =============================================================================

/** Current wallet state — single source of truth */
let _state: UnifiedWalletState = {
  address: null,
  chainId: null,
  isCorrectChain: false,
  isConnected: false,
  isConnecting: false,
  connectionSource: null,
};

/** Raw EIP-1193 provider reference */
let _rawProvider: EIP1193Provider | null = null;

/** Ethers Web3Provider wrapper */
let _web3Provider: ethers.providers.Web3Provider | null = null;

/** Ethers Signer */
let _signer: ethers.providers.JsonRpcSigner | null = null;

/** How the current connection was established */
let _connectionSource: ConnectionSource = null;

/** All subscribed React components — called on state change to trigger re-render */
const _listeners: Set<() => void> = new Set();


/** Whether event listeners are currently attached to the provider */
let _providerListenersAttached = false;

// =============================================================================
// SINGLETON INTERNALS
// =============================================================================

/**
 * Notify all subscribed hook instances to re-render.
 */
function notify() {
  _listeners.forEach((fn) => fn());
}

/**
 * Subscribe a React component to state changes.
 * Returns an unsubscribe function.
 */
function subscribe(listener: () => void): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

/**
 * Update the singleton state and notify all subscribers.
 */
function setState(
  updater: Partial<UnifiedWalletState> | ((prev: UnifiedWalletState) => UnifiedWalletState)
) {
  if (typeof updater === 'function') {
    _state = updater(_state);
  } else {
    _state = { ..._state, ...updater };
  }
  notify();
}

/**
 * Rebuild ethers objects from the current raw provider.
 */
function rebuildEthersObjects() {
  if (_rawProvider) {
    // "any" so Avalanche pair → Apertum switch does not leave the signer on 43114.
    _web3Provider = new ethers.providers.Web3Provider(_rawProvider as any, 'any');
    _signer = _web3Provider.getSigner();
  }
}

function pinAndRebuildForApertum() {
  if (_rawProvider) {
    pinWalletConnectToApertum(
      _rawProvider,
      APERTUM_CHAIN.chainId,
      APERTUM_CHAIN.rpcUrls[0]
    );
  }
  rebuildEthersObjects();
}

function isUserRejected(err: unknown): boolean {
  if (!err || typeof err !== 'object') {
    return typeof err === 'string' && /reject/i.test(err);
  }
  const code = 'code' in err ? (err as { code?: unknown }).code : undefined;
  const message = 'message' in err ? String((err as { message?: unknown }).message) : '';
  return code === 4001 || /reject/i.test(message);
}

async function requestWalletApertumChain(
  provider: EIP1193Provider
): Promise<'switched' | 'rejected' | 'failed'> {
  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: APERTUM_CHAIN.chainIdHex.toLowerCase() }],
    });
    return 'switched';
  } catch (switchError: unknown) {
    const code =
      switchError && typeof switchError === 'object' && 'code' in switchError
        ? (switchError as { code?: unknown }).code
        : undefined;
    if (code === 4902) {
      try {
        await provider.request({
          method: 'wallet_addEthereumChain',
          params: [getAddChainParams()],
        });
        return 'switched';
      } catch (addError: unknown) {
        if (isUserRejected(addError)) return 'rejected';
        console.warn('Failed to add Apertum chain:', addError);
        return 'failed';
      }
    }
    if (isUserRejected(switchError) || code === 4001) return 'rejected';
    console.warn('Failed to switch chain:', switchError);
    return 'failed';
  }
}

type WalletConnectProviderInstance = EIP1193Provider & {
  session?: unknown;
  disconnect?: () => Promise<void>;
  enable: () => Promise<unknown>;
};

// =============================================================================
// WALLETCONNECT PROVIDER FACTORY
// =============================================================================

/**
 * Dynamically import and initialize WalletConnect's EthereumProvider.
 * Uses dynamic import so the ~200KB WC bundle is only loaded when needed.
 */
async function initWalletConnectProvider(): Promise<WalletConnectProviderInstance> {
  if (!isWalletConnectConfigured()) {
    throw new Error('WalletConnect is not configured for SportsFuture.');
  }
  const { default: EthereumProvider } = await import('@walletconnect/ethereum-provider');

  // Required chain must be one Tangem/Rainbow/etc. already list, or pairing
  // dies with "unsupported networks". Apertum 2786 is optional; we add/switch
  // after the session exists. Never require ETH (1) or Polygon (137).
  const wcProvider = await EthereumProvider.init({
    projectId: WALLETCONNECT_PROJECT_ID,
    metadata: getWalletConnectMetadata(),
    showQrModal: true,
    telemetryEnabled: false,
    chains: [WALLETCONNECT_PAIR_CHAIN.chainId],
    optionalChains: [APERTUM_CHAIN.chainId, WALLETCONNECT_PAIR_CHAIN.chainId],
    rpcMap: {
      [APERTUM_CHAIN.chainId]: APERTUM_CHAIN.rpcUrls[0],
      [WALLETCONNECT_PAIR_CHAIN.chainId]: WALLETCONNECT_PAIR_CHAIN.rpcUrls[0],
    },
    methods: [
      'eth_sendTransaction',
      'eth_signTransaction',
      'eth_sign',
      'personal_sign',
      'eth_signTypedData',
      'eth_signTypedData_v4',
      'wallet_switchEthereumChain',
      'wallet_addEthereumChain',
    ],
    events: ['chainChanged', 'accountsChanged'],
  });

  return wcProvider as unknown as WalletConnectProviderInstance;
}

async function createWalletConnectProvider(): Promise<EIP1193Provider> {
  try {
    clearWalletConnectStorage();

    let wcProvider = await initWalletConnectProvider();

    if (wcProvider.session) {
      await teardownWalletConnectProvider(wcProvider);
      clearWalletConnectStorage();
      wcProvider = await initWalletConnectProvider();
    }

    await wcProvider.enable();
    return wcProvider as unknown as EIP1193Provider;
  } catch (error: any) {
    if (error?.message?.includes('User rejected') || error?.message?.includes('rejected')) {
      throw new Error('Connection rejected by user');
    }
    throw new Error(`WalletConnect initialization failed: ${error?.message || 'Unknown error'}`);
  }
}

// =============================================================================
// EVENT HANDLERS (singleton — one set of handlers for the shared provider)
// =============================================================================

function handleAccountsChanged(accounts: string[]) {
  if (!accounts || accounts.length === 0) {
    // Wallet disconnected via extension
    resetConnection();
    toast.success('Wallet disconnected');
    return;
  }

  const newAddress = ethers.utils.getAddress(accounts[0]);
  if (newAddress !== _state.address) {
    rebuildEthersObjects();
    setState({ address: newAddress });
  }
}

function handleChainChanged(chainIdValue: unknown) {
  const chainId = parseChainId(chainIdValue);
  const isCorrectChain = chainId !== null && chainId === APERTUM_CHAIN.chainId;

  rebuildEthersObjects();
  setState({ chainId, isCorrectChain });
}

function handleWCDisconnect() {
  resetConnection();
  toast.success('WalletConnect session ended');
}

// =============================================================================
// LISTENER MANAGEMENT
// =============================================================================

function attachProviderListeners(provider: EIP1193Provider, source: ConnectionSource) {
  if (_providerListenersAttached) return; // Prevent double-attach
  if (provider.on) {
    provider.on('accountsChanged', handleAccountsChanged);
    provider.on('chainChanged', handleChainChanged);
    if (source === 'walletconnect') {
      provider.on('disconnect', handleWCDisconnect);
    }
    _providerListenersAttached = true;
  }
}

function detachProviderListeners() {
  if (!_rawProvider || !_rawProvider.removeListener || !_providerListenersAttached) return;

  _rawProvider.removeListener('accountsChanged', handleAccountsChanged);
  _rawProvider.removeListener('chainChanged', handleChainChanged);
  if (_connectionSource === 'walletconnect') {
    _rawProvider.removeListener('disconnect', handleWCDisconnect);
  }
  _providerListenersAttached = false;
}

// =============================================================================
// CORE CONNECTION LOGIC
// =============================================================================

/**
 * After obtaining a provider, validate chain, extract address, build ethers objects.
 */
async function finalizeConnection(provider: EIP1193Provider, source: ConnectionSource) {
  // Get accounts
  const accounts = (await provider.request({ method: 'eth_accounts' })) as string[];
  if (!accounts || accounts.length === 0) {
    throw new Error('No accounts returned from wallet');
  }

  const address = ethers.utils.getAddress(accounts[0]); // Checksummed

  // Get chain ID
  const chainIdRaw = await provider.request({ method: 'eth_chainId' });
  const chainId = parseChainId(chainIdRaw);
  const isCorrectChain = chainId !== null && chainId === APERTUM_CHAIN.chainId;

  // Store refs
  _rawProvider = provider;
  _connectionSource = source;
  _web3Provider = new ethers.providers.Web3Provider(provider as any);
  _signer = _web3Provider.getSigner();

  // Update state (notifies all subscribers)
  setState({
    address,
    chainId,
    isCorrectChain,
    isConnected: true,
    isConnecting: false,
    connectionSource: source,
  });

  return { address, chainId, isCorrectChain };
}

/**
 * Reset all connection state.
 */
function resetConnection() {
  detachProviderListeners();

  _rawProvider = null;
  _web3Provider = null;
  _signer = null;
  _connectionSource = null;

  setState({
    address: null,
    chainId: null,
    isCorrectChain: false,
    isConnected: false,
    isConnecting: false,
    connectionSource: null,
  });
}

// =============================================================================
// PUBLIC ACTIONS (called by the hook, operate on singleton state)
// =============================================================================

/**
 * Connect using a discovered wallet entry.
 */
async function connect(wallet: DiscoveredWallet): Promise<void> {
  setState({ isConnecting: true });

  try {
    let provider: EIP1193Provider;
    let source: ConnectionSource;

    if (wallet.type === 'walletconnect') {
      // ── WalletConnect Path ──
      source = 'walletconnect';
      provider = await createWalletConnectProvider();
    } else {
      // ── Injected Wallet Path ──
      source = 'injected';

      if (!wallet.provider) {
        throw new Error(`No provider available for ${wallet.name}`);
      }

      provider = wallet.provider;

      // Request accounts (triggers the wallet popup)
      await provider.request({ method: 'eth_requestAccounts' });
    }

    // Finalize: get address, chainId, build ethers objects
    const { isCorrectChain } = await finalizeConnection(provider, source);

    // Attach event listeners
    attachProviderListeners(provider, source);

    // Re-check chain after connect (mobile wallets may report decimal chain IDs)
    const chainAfterConnect = parseChainId(
      await provider.request({ method: 'eth_chainId' })
    );
    const onApertum =
      chainAfterConnect !== null && chainAfterConnect === APERTUM_CHAIN.chainId;

    if (onApertum) {
      pinAndRebuildForApertum();
      setState({ chainId: chainAfterConnect, isCorrectChain: true });
    } else if (!isCorrectChain) {
      const switchResult = await requestWalletApertumChain(provider);
      if (switchResult === 'switched') {
        pinAndRebuildForApertum();
        const chainAfterSwitch = parseChainId(
          await provider.request({ method: 'eth_chainId' }).catch(() => APERTUM_CHAIN.chainId)
        );
        const nowOnApertum =
          chainAfterSwitch === null || chainAfterSwitch === APERTUM_CHAIN.chainId;
        setState({
          chainId: chainAfterSwitch ?? APERTUM_CHAIN.chainId,
          isCorrectChain: nowOnApertum,
        });
        if (!nowOnApertum) {
          toast.error(
            'Connected, but this wallet is still on Avalanche. Switch to Apertum (APTM gas, not AVAX).'
          );
        }
      } else if (switchResult === 'failed') {
        toast.error(
          `${wallet.name} did not switch to Apertum (2786). Open Apertum in the wallet — gas is APTM, not AVAX.`
        );
      }
    }

    toast.success(`Connected via ${wallet.name}`);
  } catch (error: any) {
    console.error('Wallet connection error:', error);

    if (error.code === 4001 || error.message?.includes('rejected')) {
      toast.error('Connection cancelled');
    } else {
      toast.error(error.message || 'Failed to connect wallet');
    }
  } finally {
    // Guarantee isConnecting is always reset, even for unexpected errors
    if (_state.isConnecting && !_state.isConnected) {
      setState({ isConnecting: false });
    }
  }
}

/**
 * Disconnect the active wallet.
 */
async function disconnect(): Promise<void> {
  // Detach listeners first
  detachProviderListeners();

  if (_connectionSource === 'walletconnect') {
    await teardownWalletConnectProvider(_rawProvider);
  }

  resetConnection();
  toast.success('Wallet disconnected');
}

/**
 * Switch to the Apertum network.
 */
async function switchToApertum(): Promise<void> {
  if (!_rawProvider) {
    toast.error('No wallet connected');
    return;
  }

  try {
    const currentChain = parseChainId(
      await _rawProvider.request({ method: 'eth_chainId' })
    );
    if (currentChain === APERTUM_CHAIN.chainId) {
      pinAndRebuildForApertum();
      setState({ chainId: currentChain, isCorrectChain: true });
      return;
    }

    await _rawProvider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: APERTUM_CHAIN.chainIdHex }],
    });

    const chainAfterSwitch = parseChainId(
      await _rawProvider.request({ method: 'eth_chainId' })
    );
    if (chainAfterSwitch !== null) {
      setState({
        chainId: chainAfterSwitch,
        isCorrectChain: chainAfterSwitch === APERTUM_CHAIN.chainId,
      });
    }
  } catch (error: any) {
    if (error.code === 4902) {
      try {
        await _rawProvider.request({
          method: 'wallet_addEthereumChain',
          params: [getAddChainParams()],
        });
        const chainAfterAdd = parseChainId(
          await _rawProvider.request({ method: 'eth_chainId' })
        );
        if (chainAfterAdd !== null) {
          setState({
            chainId: chainAfterAdd,
            isCorrectChain: chainAfterAdd === APERTUM_CHAIN.chainId,
          });
        }
      } catch {
        toast.error('Failed to add Apertum network');
      }
    } else if (error.code !== 4001) {
      toast.error('Failed to switch network');
    }
  }
}

/**
 * Get the current ethers Web3Provider (or null if not connected).
 */
function getProvider(): ethers.providers.Web3Provider | null {
  return _web3Provider;
}

/**
 * Get the current ethers Signer (or null if not connected).
 */
function getSigner(): ethers.providers.JsonRpcSigner | null {
  return _signer;
}

/**
 * Get the raw EIP-1193 provider.
 */
function getRawProvider(): EIP1193Provider | null {
  return _rawProvider;
}

// =============================================================================
// REACT HOOK (thin wrapper — subscribes to singleton state)
// =============================================================================

/**
 * Hook to access the unified wallet state and actions.
 *
 * SINGLETON: Every call to this hook shares the same underlying state.
 * When one component connects a wallet, all components using this hook
 * will re-render with the updated state.
 *
 * @example
 * ```tsx
 * // In ANSWalletButton:
 * const unifiedWallet = useUnifiedWallet();
 * // unifiedWallet.isConnected, .address, .connect(), etc.
 *
 * // In WalletContext:
 * const unifiedWallet = useUnifiedWallet();
 * // Same state! When ANSWalletButton connects, this sees it too.
 * ```
 */
export function useUnifiedWallet(): UnifiedWalletHook {
  // Force re-render when singleton state changes
  const [, forceRender] = useState(0);

  useEffect(() => {
    // Subscribe to singleton state changes
    const unsubscribe = subscribe(() => forceRender((n) => n + 1));
    return unsubscribe;
  }, []);

  // Return current singleton state + stable action references
  // (actions are module-level functions, so they're referentially stable)
  return {
    // State (read from singleton)
    address: _state.address,
    chainId: _state.chainId,
    isCorrectChain: _state.isCorrectChain,
    isConnected: _state.isConnected,
    isConnecting: _state.isConnecting,
    connectionSource: _state.connectionSource,

    // Actions (module-level functions — stable references)
    connect,
    disconnect,
    switchToApertum,
    getProvider,
    getSigner,
    getRawProvider,
  };
}
